import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { reports, type MapKpi, type MapLocation, type MapProvince, type MapResults } from '../api/reports';
import { fold } from '../map/match';
import { MAP_BOUNDS, MAP_HEIGHT, MAP_WIDTH, PROVINCES, type ProvinceShape } from '../map/turkiye';
import {
  IconArrowDown,
  IconArrowUp,
  IconMinus,
  IconPlus,
  IconRefreshCw,
  IconTriangleAlert,
  IconX,
} from '../icons';
import { fixed, formatDate } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { BandChip } from '../ui/BandChip';
import { Button, IconButton } from '../ui/Button';
import { Alert } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { Table, type Column } from '../ui/Table';

/**
 * Türkiye Haritası — every store at once.
 *
 * <p>Everything here is already answerable store by store from the visit-results report.
 * What the map adds is the question a list cannot answer: <i>where</i>. Three hundred rows
 * sorted by score are three hundred rows; the same data on a map shows a coast doing well
 * and an inland region nobody has visited since spring.</p>
 *
 * <p><b>Why provinces and not pins.</b> Barely any store has coordinates yet. So a store
 * sits at its province, and zooming in pulls that province's stores apart — which is what
 * makes İstanbul's two dozen readable without any of them drifting into Kocaeli. Once
 * addresses are turned into coordinates the same markers move to their real places and
 * nothing else about this screen changes.</p>
 */

/** How far in the map will go. Beyond this a province fills the frame and says nothing more. */
const MAX_ZOOM = 12;

/** Past this, a province's stores are drawn one by one rather than as a counted bubble. */
const SPREAD_AT = 2.4;

/** Past this, every province is named, not only the ones with stores. */
const LABEL_ALL_AT = 1.8;

/** The drawn shape is this many units wide for every unit tall. */
const RATIO = MAP_WIDTH / MAP_HEIGHT;

interface View {
  x: number;
  y: number;
  w: number;
}

const WHOLE: View = { x: 0, y: 0, w: MAP_WIDTH };

/**
 * A real coordinate into the drawn view box.
 *
 * <p>The same projection the shapes were generated with, reading the bounds the generator
 * wrote down rather than repeating them here — a regenerated map would otherwise leave the
 * few coordinate-carrying stores sitting in the sea, and nothing would say why.</p>
 */
function project(latitude: number, longitude: number): [number, number] {
  const scale = MAP_WIDTH / ((MAP_BOUNDS.east - MAP_BOUNDS.west) * MAP_BOUNDS.squeeze);

  return [
    (longitude - MAP_BOUNDS.west) * MAP_BOUNDS.squeeze * scale,
    (MAP_BOUNDS.north - latitude) * scale,
  ];
}

const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/** Keeps the frame inside the country, so panning cannot lose the map off an edge. */
function settle(view: View): View {
  const w = clamp(view.w, MAP_WIDTH / MAX_ZOOM, MAP_WIDTH);
  const h = w / RATIO;

  return {
    w,
    x: clamp(view.x, 0, MAP_WIDTH - w),
    y: clamp(view.y, 0, MAP_HEIGHT - h),
  };
}

/** The frame that holds a province, with room around it so it is not touching the edges. */
function frameOf([x0, y0, x1, y1]: readonly [number, number, number, number]): View {
  const w = Math.max((x1 - x0) * 1.5, (y1 - y0) * RATIO * 1.5, MAP_WIDTH / MAX_ZOOM);

  return settle({ w, x: (x0 + x1) / 2 - w / 2, y: (y0 + y1) / 2 - w / RATIO / 2 });
}

/**
 * Where to draw each store of a province.
 *
 * <p>A store with coordinates goes where it is. The rest are laid out around the province
 * centre in rings, spaced by how far the map is zoomed in, so they pull apart as somebody
 * zooms rather than staying one smudge.</p>
 */
function placed(centre: ProvinceShape, stores: MapLocation[], scale: number) {
  const dots: { store: MapLocation; x: number; y: number }[] = [];
  const loose: MapLocation[] = [];

  for (const store of stores) {
    if (store.latitude !== null && store.longitude !== null) {
      const [x, y] = project(store.latitude, store.longitude);
      dots.push({ store, x, y });
    } else {
      loose.push(store);
    }
  }

  if (loose.length === 1) {
    dots.push({ store: loose[0]!, x: centre.cx, y: centre.cy });
    return dots;
  }

  const step = 9 / Math.sqrt(scale);

  loose.forEach((store, i) => {
    const ring = Math.floor(i / 6);
    const place = i % 6;
    const radius = step * (ring + 1);
    const angle = (place / 6) * Math.PI * 2 + ring * 0.5;

    dots.push({
      store,
      x: centre.cx + Math.cos(angle) * radius,
      y: centre.cy + Math.sin(angle) * radius * 0.8,
    });
  });

  return dots;
}

/**
 * How full a province looks when nothing has been scored there yet.
 *
 * <p>Colouring strictly by score means a chain that has not been audited yet opens a blank
 * country — which reads as a broken screen rather than as "no visits". So until there is a
 * score, a province is tinted by how many stores are in it, and the legend says so. The
 * moment a visit lands, that province flips to its band colour.</p>
 */
function weight(stores: number): string {
  if (stores >= 10) return 'nv-map__province--c4';
  if (stores >= 5) return 'nv-map__province--c3';
  if (stores >= 2) return 'nv-map__province--c2';
  return 'nv-map__province--c1';
}

type Hovered =
  | { kind: 'store'; store: MapLocation; at: { x: number; y: number } }
  | { kind: 'province'; province: MapProvince | null; name: string; at: { x: number; y: number } };

export function LocationMap() {
  const t = useTranslate();

  const [data, setData] = useState<MapResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const [view, setView] = useState<View>(WHOLE);

  /**
   * The province whose stores are listed under the map, by folded name.
   *
   * <p>The hover card can only ever show the first few — it follows the cursor and takes no
   * clicks, so it cannot be scrolled. İstanbul has twenty-eight stores and "+22 more" is
   * not an answer. Clicking a province frames it and lists all of them below, where there
   * is room for the name, the district and the score.</p>
   */
  const [opened, setOpened] = useState<string | null>(null);

  const frame = useRef<HTMLDivElement | null>(null);
  const svg = useRef<SVGSVGElement | null>(null);
  const dragging = useRef<{ x: number; y: number } | null>(null);

  /** Where the press started, in screen pixels, to tell a click from a drag. */
  const pressed = useRef<{ x: number; y: number } | null>(null);

  /** True once the pointer has travelled far enough that this was a drag, not a click. */
  const panned = useRef(false);

  /** The pointer is inside the hover card, so the shapes underneath must not steal it. */
  const inCard = useRef(false);

  /** Every finger currently down, so two of them can be told from one. */
  const touches = useRef(new Map<number, { x: number; y: number }>());

  /** The last pinch, to measure the next one against. */
  const pinch = useRef<{ span: number; middle: { x: number; y: number } } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await reports.map());
      setError(null);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const height = view.w / RATIO;
  const scale = MAP_WIDTH / view.w;

  // Both directions, built once: the shapes are matched to the data by folded name, and the
  // card needs to get from a province back to its stores.
  const byProvince = useMemo(() => {
    const provinces = new Map<string, MapProvince>();
    for (const p of data?.provinces ?? []) provinces.set(fold(p.name), p);

    const stores = new Map<string, MapLocation[]>();
    for (const l of data?.locations ?? []) {
      if (!l.city) continue;
      const key = fold(l.city);
      const list = stores.get(key);
      if (list) list.push(l);
      else stores.set(key, [l]);
    }

    return { provinces, stores };
  }, [data]);

  /** Stores whose city matches no drawn province. Said out loud rather than dropped. */
  const unplaced = useMemo(() => {
    const drawn = new Set(PROVINCES.map((p) => fold(p.name)));

    return (data?.locations ?? []).filter((l) => !l.city || !drawn.has(fold(l.city)));
  }, [data]);

  // Registered by hand rather than with onWheel, because React's wheel listener is passive
  // and a passive listener cannot stop the page scrolling behind the map.
  useEffect(() => {
    const element = svg.current;
    if (!element) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();

      setView((current) => {
        const wanted = clamp(
          current.w / Math.exp(-event.deltaY * 0.0015),
          MAP_WIDTH / MAX_ZOOM,
          MAP_WIDTH,
        );

        const box = element.getBoundingClientRect();
        const at = {
          x: current.x + ((event.clientX - box.left) / box.width) * current.w,
          y: current.y + ((event.clientY - box.top) / box.height) * (current.w / RATIO),
        };

        // The point under the cursor stays under the cursor. Zooming to the centre instead
        // means aiming at a province and watching it slide away.
        const ratio = wanted / current.w;

        return settle({
          w: wanted,
          x: at.x - (at.x - current.x) * ratio,
          y: at.y - (at.y - current.y) * ratio,
        });
      });
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // A second finger is a pinch, not a drag. Hand back the first finger's capture or the
    // second one's moves never arrive.
    if (touches.current.size > 1) {
      dragging.current = null;
      pinch.current = null;
      return;
    }

    dragging.current = { x: event.clientX, y: event.clientY };
    pressed.current = { x: event.clientX, y: event.clientY };
    panned.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  /** Two fingers: the distance between them drives the zoom, their middle stays put. */
  const pinched = () => {
    const [a, b] = [...touches.current.values()];
    if (!a || !b) return;

    const box = svg.current?.getBoundingClientRect();
    if (!box) return;

    const span = Math.hypot(a.x - b.x, a.y - b.y);
    const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

    const was = pinch.current;
    pinch.current = { span, middle };

    if (!was || was.span === 0) return;

    panned.current = true;
    setHovered(null);

    setView((current) => {
      const wanted = clamp(current.w * (was.span / span), MAP_WIDTH / MAX_ZOOM, MAP_WIDTH);

      const at = {
        x: current.x + ((middle.x - box.left) / box.width) * current.w,
        y: current.y + ((middle.y - box.top) / box.height) * (current.w / RATIO),
      };

      const ratio = wanted / current.w;

      return settle({
        w: wanted,
        x: at.x - (at.x - current.x) * ratio,
        y: at.y - (at.y - current.y) * ratio,
      });
    });
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (touches.current.has(event.pointerId))
      touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (touches.current.size > 1) {
      pinched();
      return;
    }

    const from = dragging.current;
    if (!from) return;

    const box = svg.current?.getBoundingClientRect();
    if (!box) return;

    const dx = ((event.clientX - from.x) / box.width) * view.w;
    const dy = ((event.clientY - from.y) / box.height) * height;

    // Measured from where the press started and in screen pixels, not map units. The first
    // version compared map units since the last move: at full zoom one pixel of mouse
    // jitter is two thirds of a unit, so an ordinary click registered as a drag and was
    // thrown away.
    const start = pressed.current;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 4)
      panned.current = true;

    dragging.current = { x: event.clientX, y: event.clientY };
    setHovered(null);
    setView((current) => settle({ ...current, x: current.x - dx, y: current.y - dy }));
  };

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    touches.current.delete(event.pointerId);
    if (touches.current.size < 2) pinch.current = null;

    dragging.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const point = (event: { clientX: number; clientY: number }) => {
    const box = frame.current?.getBoundingClientRect();
    return box ? { x: event.clientX - box.left, y: event.clientY - box.top } : { x: 0, y: 0 };
  };

  /**
   * Shows the card, unless the pointer has moved onto the card itself.
   *
   * <p>Anchored on entry rather than on every move: a card that slides away as somebody
   * reaches for it is a card they can never reach, and this one has a line in it worth
   * clicking.</p>
   */
  const enter = (next: Hovered) => {
    if (!inCard.current) setHovered(next);
  };

  /**
   * Whatever the hover card is describing, whether or not the pointer is over the shape.
   *
   * <p>The card follows the cursor and covers the province it is about, and it takes no
   * clicks of its own — a card that did would steal the pointer and flicker. So the click
   * is answered by the map and resolved from what is being hovered, which is the thing
   * somebody means when they click while reading it.</p>
   */
  const clicked = () => {
    if (panned.current || !hovered) return;

    const name = hovered.kind === 'province' ? hovered.name : hovered.store.city;
    if (!name) return;

    const shape = PROVINCES.find((p) => fold(p.name) === fold(name));
    if (shape) show(shape);
  };

  /** Clicking a province does both things somebody means by it: frame it, and list it. */
  const show = (shape: ProvinceShape) => {
    const key = fold(shape.name);

    setView(frameOf(shape.box));
    setOpened(byProvince.stores.has(key) ? key : null);
  };

  /** The buttons zoom about the middle, because there is no cursor to zoom about. */
  const zoom = (by: number) =>
    setView((current) =>
      settle({
        w: current.w / by,
        x: current.x + (current.w - current.w / by) / 2,
        y: current.y + (current.w / RATIO - current.w / by / RATIO) / 2,
      }),
    );

  const summary = data?.summary;

  return (
    <>
      <PageHeader
        crumbs={[t('app.reports')]}
        title={t('map.title')}
        subtitle={t('map.subtitle')}
        action={
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <IconRefreshCw className={loading ? 'nv-spin' : undefined} />
            {t('map.refresh')}
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {summary ? (
        <div className="nv-mapstats">
          <Stat label={t('map.stores')} value={String(summary.locations)} />
          <Stat
            label={t('map.visited')}
            value={`${summary.visited} / ${summary.locations}`}
            tone={summary.visited < summary.locations ? 'warn' : undefined}
          />
          <Stat
            label={t('map.averageScore')}
            value={summary.averageScore !== null ? fixed(summary.averageScore, 2) : '—'}
          />
          <Stat label={t('map.openActions')} value={String(summary.openActions)} />
          <Stat
            label={t('map.overdueActions')}
            value={String(summary.overdueActions)}
            tone={summary.overdueActions > 0 ? 'bad' : undefined}
          />
        </div>
      ) : null}

      <Card>
        <div
          className="nv-map"
          ref={frame}
          onMouseLeave={() => {
            inCard.current = false;
            setHovered(null);
          }}
        >
          <svg
            ref={svg}
            viewBox={`${view.x} ${view.y} ${view.w} ${height}`}
            className="nv-map__svg"
            role="img"
            aria-label={t('map.title')}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClick={clicked}
            onDoubleClick={() => {
              setView(WHOLE);
              setOpened(null);
            }}
          >
            <g className="nv-map__land">
              {PROVINCES.map((shape) => {
                const province = byProvince.provinces.get(fold(shape.name));
                const scored = province?.bandColour != null && province.averageScore !== null;

                return (
                  <path
                    key={shape.plate}
                    d={shape.path}
                    className={
                      province
                        ? scored
                          ? 'nv-map__province'
                          : `nv-map__province ${weight(province.locations)}`
                        : 'nv-map__province nv-map__province--none'
                    }
                    style={{
                      ...(scored ? { fill: province.bandColour! } : {}),
                      strokeWidth: 0.8 / scale,
                    }}
                    onMouseEnter={(e) => enter({
                      kind: 'province',
                      province: province ?? null,
                      name: shape.name,
                      at: point(e),
                    })}
                  />
                );
              })}
            </g>

            <g className="nv-map__labels">
              {PROVINCES.map((shape) => {
                const stores = byProvince.stores.get(fold(shape.name));
                if (!stores && scale < LABEL_ALL_AT) return null;

                // Sized against the zoom so it stays the same on screen: a label that grew
                // with the map would be unreadable at one end and invisible at the other.
                return (
                  <text
                    key={shape.plate}
                    x={shape.cx}
                    y={shape.cy - (stores ? 11 : 0) / scale}
                    style={{ fontSize: `${(stores ? 10 : 8.5) / scale}px` }}
                    className={stores ? 'nv-map__label' : 'nv-map__label nv-map__label--quiet'}
                  >
                    {shape.name}
                  </text>
                );
              })}
            </g>

            <g className="nv-map__marks">
              {PROVINCES.map((shape) => {
                const stores = byProvince.stores.get(fold(shape.name));
                if (!stores || stores.length === 0) return null;

                // A counted bubble until the map is zoomed in far enough for the dots to
                // stand apart. Two dozen overlapping dots are a smudge, not information.
                if (stores.length > 4 && scale < SPREAD_AT) {
                  const overdue = stores.some((s) => s.overdueActions > 0);

                  return (
                    <g
                      key={`c-${shape.plate}`}
                      className={
                        overdue ? 'nv-map__cluster nv-map__cluster--overdue' : 'nv-map__cluster'
                      }
                      onMouseEnter={(e) => enter({
                        kind: 'province',
                        province: byProvince.provinces.get(fold(shape.name)) ?? null,
                        name: shape.name,
                        at: point(e),
                      })}
                    >
                      <circle
                        cx={shape.cx}
                        cy={shape.cy}
                        r={(9 + Math.min(stores.length, 24) * 0.22) / scale}
                        style={{ strokeWidth: 1.6 / scale }}
                      />
                      <text
                        x={shape.cx}
                        y={shape.cy + 3.4 / scale}
                        style={{ fontSize: `${9.5 / scale}px` }}
                      >
                        {stores.length}
                      </text>
                    </g>
                  );
                }

                return (
                  <g key={`s-${shape.plate}`}>
                    {placed(shape, stores, scale).map(({ store, x, y }) => (
                      <circle
                        key={store.id}
                        cx={x}
                        cy={y}
                        r={4.2 / scale}
                        style={{ strokeWidth: 1.4 / scale }}
                        className={
                          store.overdueActions > 0
                            ? 'nv-map__dot nv-map__dot--overdue'
                            : store.lastVisitAt
                              ? 'nv-map__dot'
                              : 'nv-map__dot nv-map__dot--unvisited'
                        }
                        onMouseEnter={(e) => enter({ kind: 'store', store, at: point(e) })}
                      />
                    ))}
                  </g>
                );
              })}
            </g>
          </svg>

          <div className="nv-map__zoom">
            <IconButton label={t('map.zoomIn')} icon={IconPlus} onClick={() => zoom(1.6)} />
            <IconButton label={t('map.zoomOut')} icon={IconMinus} onClick={() => zoom(1 / 1.6)} />
            <IconButton
              label={t('map.reset')}
              icon={IconRefreshCw}
              onClick={() => {
                setView(WHOLE);
                setOpened(null);
              }}
              disabled={view.w === MAP_WIDTH && opened === null}
            />
          </div>

          {hovered ? (
            <HoverCard
              hovered={hovered}
              kpis={data?.kpis ?? []}
              stores={
                hovered.kind === 'province' ? (byProvince.stores.get(fold(hovered.name)) ?? []) : []
              }
              onEnter={() => (inCard.current = true)}
              onLeave={() => {
                inCard.current = false;
                setHovered(null);
              }}
              onOpenAll={() => {
                const name = hovered.kind === 'province' ? hovered.name : hovered.store.city;
                const shape = name
                  ? PROVINCES.find((x) => fold(x.name) === fold(name))
                  : undefined;

                inCard.current = false;
                setHovered(null);
                if (shape) show(shape);
              }}
            />
          ) : null}
        </div>

        <div className="nv-map__legend">
          <span className="nv-map__legend-title">{t('map.legend')}</span>

          {(data?.provinces ?? [])
            .filter((p) => p.band && p.averageScore !== null)
            .map((p) => ({ band: p.band!, colour: p.bandColour }))
            .filter((b, i, all) => all.findIndex((x) => x.band === b.band) === i)
            .sort((a, b) => a.band.localeCompare(b.band, 'tr'))
            .map((b) => (
              <span key={b.band} className="nv-map__legend-item">
                <i style={b.colour ? { background: b.colour } : undefined} />
                {b.band}
              </span>
            ))}

          <span className="nv-map__legend-item">
            <i className="nv-map__swatch nv-map__province--c1" />
            <i className="nv-map__swatch nv-map__province--c3" />
            <i className="nv-map__swatch nv-map__province--c4" />
            {t('map.unscored')}
          </span>

          <span className="nv-map__legend-item">
            <i className="nv-map__swatch nv-map__province--none" />
            {t('map.noStore')}
          </span>

        </div>

        <div className="nv-map__legend">
          <span className="nv-map__legend-title">{t('map.dots')}</span>

          <span className="nv-map__legend-item">
            <i className="nv-map__pip" />
            {t('map.dotVisited')}
          </span>
          <span className="nv-map__legend-item">
            <i className="nv-map__pip nv-map__pip--overdue" />
            {t('map.dotOverdue')}
          </span>
          <span className="nv-map__legend-item">
            <i className="nv-map__pip nv-map__pip--unvisited" />
            {t('map.dotUnvisited')}
          </span>

          <span className="nv-map__legend-hint">{t('map.howTo')}</span>
        </div>

        {opened ? (
          <ProvinceStores
            name={PROVINCES.find((p) => fold(p.name) === opened)?.name ?? opened}
            stores={byProvince.stores.get(opened) ?? []}
            onClose={() => setOpened(null)}
          />
        ) : null}

        {unplaced.length > 0 ? (
          <p className="nv-map__unplaced">
            <IconTriangleAlert />
            {t('map.unplaced', { count: String(unplaced.length) })}{' '}
            {unplaced.map((l) => l.code).join(', ')}
          </p>
        ) : null}
      </Card>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' | 'bad' }) {
  return (
    <div className={tone ? `nv-mapstat nv-mapstat--${tone}` : 'nv-mapstat'}>
      <span className="nv-mapstat__label">{label}</span>
      <strong className="nv-mapstat__value">{value}</strong>
    </div>
  );
}

/**
 * What a store or a province says when somebody points at it.
 *
 * <p>Follows the cursor and flips to the other side when it would run off the edge — a card
 * clipped by the border of the map is a card nobody can read, and İstanbul and Hakkari are
 * both at an edge.</p>
 */
function HoverCard({
  hovered,
  kpis,
  stores,
  onEnter,
  onLeave,
  onOpenAll,
}: {
  hovered: Hovered;
  kpis: MapKpi[];
  stores: MapLocation[];
  onEnter: () => void;
  onLeave: () => void;
  onOpenAll: () => void;
}) {
  const t = useTranslate();

  const flip = hovered.at.x > 620;
  const high = hovered.at.y > 300;

  const style: CSSProperties = {
    left: hovered.at.x + (flip ? -16 : 16),
    top: hovered.at.y + (high ? -16 : 16),
    transform: `translate(${flip ? '-100%' : '0'}, ${high ? '-100%' : '0'})`,
  };

  if (hovered.kind === 'province') {
    const p = hovered.province;

    // A province with no stores says its name and nothing else. The alternative — a card
    // announcing "no location" over two thirds of the country — is noise with a border
    // around it.
    if (!p) {
      return (
        <div className="nv-map__card nv-map__card--bare" style={style}>
          {hovered.name}
        </div>
      );
    }

    return (
      <div className="nv-map__card" style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <header>
          <strong>{hovered.name}</strong>
          {p.band && p.averageScore !== null ? (
            <BandChip band={p.band} colour={p.bandColour} />
          ) : null}
        </header>

        <dl>
          <Line label={t('map.stores')} value={String(p.locations)} />
          <Line
            label={t('map.averageScore')}
            value={p.averageScore !== null ? fixed(p.averageScore, 2) : t('map.never')}
            tone={p.averageScore === null ? 'warn' : undefined}
          />
          <Line
            label={t('map.openActions')}
            value={
              p.overdueActions > 0
                ? t('map.openWithOverdue', {
                    open: String(p.openActions),
                    overdue: String(p.overdueActions),
                  })
                : String(p.openActions)
            }
            tone={p.overdueActions > 0 ? 'bad' : undefined}
          />
        </dl>

        {stores.length > 1 ? (
          <ul className="nv-map__card-list">
            {stores.slice(0, 6).map((s) => (
              <li key={s.id}>
                <span>
                  <b>{s.code}</b> {s.name}
                </span>
                <em>{s.score !== null ? fixed(s.score, 1) : '—'}</em>
              </li>
            ))}
            {stores.length > 6 ? (
              <li>
                <button type="button" className="nv-map__card-all" onClick={onOpenAll}>
                  {t('map.andMore', { count: String(stores.length - 6) })} · {t('map.clickToList')}
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    );
  }

  const s = hovered.store;

  return (
    <div className="nv-map__card" style={style} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <header>
        <strong>{s.code}</strong>
        {s.band ? <BandChip band={s.band} colour={s.bandColour} /> : null}
      </header>

      <p className="nv-map__card-name">{s.name}</p>

      <dl>
        {kpis.map((kpi) => (
          <Kpi key={kpi} kpi={kpi} store={s} />
        ))}
      </dl>
    </div>
  );
}

/** One configured figure. Which of these appear is the customer's own setting. */
function Kpi({ kpi, store }: { kpi: MapKpi; store: MapLocation }) {
  const t = useTranslate();

  if (kpi === 'LastVisit') {
    return (
      <Line
        label={t('map.lastVisit')}
        value={
          store.lastVisitAt
            ? `${formatDate(store.lastVisitAt)} · ${store.score !== null ? fixed(store.score, 1) : '—'}`
            : t('map.never')
        }
        tone={store.lastVisitAt ? undefined : 'warn'}
      />
    );
  }

  if (kpi === 'OpenActions') {
    return (
      <Line
        label={t('map.openActions')}
        value={
          store.overdueActions > 0
            ? t('map.openWithOverdue', {
                open: String(store.openActions),
                overdue: String(store.overdueActions),
              })
            : String(store.openActions)
        }
        tone={store.overdueActions > 0 ? 'bad' : undefined}
      />
    );
  }

  if (kpi === 'Trend') {
    const arrow =
      store.trend === 'Improved' ? (
        <IconArrowUp />
      ) : store.trend === 'Declined' ? (
        <IconArrowDown />
      ) : store.trend === 'Steady' ? (
        <span aria-hidden>=</span>
      ) : null;

    return (
      <Line
        label={t('map.trend')}
        value={
          store.previousScore !== null
            ? `${fixed(store.previousScore, 1)} → ${store.score !== null ? fixed(store.score, 1) : '—'}`
            : t('map.firstVisit')
        }
        icon={arrow}
        tone={store.trend === 'Declined' ? 'bad' : undefined}
      />
    );
  }

  return (
    <Line
      label={t('map.thisYear')}
      value={
        store.visitsThisYear > 0
          ? t('map.visitsAndAverage', {
              count: String(store.visitsThisYear),
              average: store.averageThisYear !== null ? fixed(store.averageThisYear, 2) : '—',
            })
          : t('map.noneThisYear')
      }
      tone={store.visitsThisYear === 0 ? 'warn' : undefined}
    />
  );
}

function Line({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon?: ReactNode;
  tone?: 'warn' | 'bad';
}) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={tone ? `nv-map__line--${tone}` : undefined}>
        {icon}
        {value}
      </dd>
    </>
  );
}

/**
 * Every store of one province, under the map.
 *
 * <p>Here rather than in the hover card because this is where there is room: a card that
 * follows the cursor cannot be scrolled, and cutting the list at six turns the two
 * provinces that matter most — İstanbul and Ankara — into "+22 more".</p>
 */
function ProvinceStores({
  name,
  stores,
  onClose,
}: {
  name: string;
  stores: MapLocation[];
  onClose: () => void;
}) {
  const t = useTranslate();
  const navigate = useNavigate();

  const columns: Column<MapLocation>[] = [
    { id: 'code', header: t('map.code'), cell: (s) => s.code, className: 'is-primary', width: 110 },
    { id: 'name', header: t('map.name'), cell: (s) => s.name },
    { id: 'district', header: t('map.district'), cell: (s) => s.district ?? '—', className: 'is-muted' },
    { id: 'type', header: t('map.locationType'), cell: (s) => s.locationType ?? '—', className: 'is-muted' },
    {
      id: 'visit',
      header: t('map.lastVisit'),
      cell: (s) => (s.lastVisitAt ? formatDate(s.lastVisitAt) : t('map.never')),
      className: 'is-muted',
    },
    {
      id: 'score',
      header: t('map.score'),
      align: 'right',
      cell: (s) =>
        s.score !== null ? (
          <span className="nv-badge-group">
            {fixed(s.score, 1)}
            {s.band ? <BandChip band={s.band} colour={s.bandColour} /> : null}
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      header: t('map.openActions'),
      align: 'right',
      cell: (s) =>
        s.overdueActions > 0
          ? t('map.openWithOverdue', {
              open: String(s.openActions),
              overdue: String(s.overdueActions),
            })
          : String(s.openActions),
    },
  ];

  return (
    <section className="nv-mappanel">
      <header>
        <h3>
          {name} <span>{t('map.storeCount', { count: String(stores.length) })}</span>
        </h3>
        <IconButton label={t('common.close')} icon={IconX} onClick={onClose} />
      </header>

      <Table
        caption={t('map.storesOf', { name })}
        columns={columns}
        rows={stores}
        getRowId={(s) => s.id}
        status="ready"
        emptyState={null}
        // Somewhere to go. A map that finds the store with the worst score and then leaves
        // you to type its name into another screen has stopped halfway.
        onRowClick={(s) => navigate(`/ziyaret-sonuclari?locationId=${s.id}`)}
      />

      <p className="nv-mappanel__hint">{t('map.rowHint')}</p>
    </section>
  );
}
